import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { CleanSidebar, ErrorBoundary } from '../components';
import type { NavItem } from '../components';
import { useAuth } from '../auth';
import { useOffices } from '../offices';
import { useCommonT } from '../i18n';
import { useIsMobile } from '../hooks/useIsMobile';

/**
 * Shared portal shell: the navy CleanSidebar owns navigation (active item derived
 * from the route by NavLink); the screen content renders in <Outlet/> on the warm
 * paper ground.
 *
 * AgentLayout / AdminLayout wrap this with their own nav model + labels.
 */
export interface PortalLayoutProps {
  items: readonly NavItem[];
  /** Small label beside the wordmark ("Iberia" / "Agent portal"). */
  brandLabel: string;
  /** Localized role name, used as the fallback user subtitle. */
  roleLabel: string;
  userSubtitle?: string;
}

export function PortalLayout({ items, brandLabel, roleLabel, userSubtitle }: PortalLayoutProps) {
  const { user, logout } = useAuth();
  const { officeName } = useOffices();
  const t = useCommonT();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const displayName = user?.fullName ?? user?.email ?? 'User';

  // Close the mobile drawer whenever the route changes.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  const signOutBtn = (
    <button
      type="button"
      className="btn sm"
      onClick={() => void logout()}
      style={{ background: 'transparent', color: 'var(--side-text)', borderColor: 'rgba(255,255,255,.14)', boxShadow: 'none' }}
      title={t('sign_out')}
      aria-label={t('sign_out')}
    >
      ⎋
    </button>
  );

  const sidebar = (
    <CleanSidebar
      items={items}
      role={brandLabel}
      user={displayName}
      userSubtitle={userSubtitle ?? (user?.office ? `${roleLabel} · ${officeName(user.office)}` : roleLabel)}
      footer={signOutBtn}
      onNavigate={isMobile ? () => setDrawerOpen(false) : undefined}
    />
  );

  const content = (
    <div
      className="col grow"
      style={{
        background: 'var(--paper)',
        // On mobile, allow the content to pan (some screens have wide rails/tables).
        overflow: isMobile ? 'auto' : 'hidden',
        minWidth: 0,
      }}
    >
      <ErrorBoundary resetKey={location.pathname}>
        <Outlet />
      </ErrorBoundary>
    </div>
  );

  // ── Desktop: fixed sidebar + content row ──────────────────────────────────
  if (!isMobile) {
    return (
      <div className="cl full row" style={{ height: '100vh' }}>
        {sidebar}
        {content}
      </div>
    );
  }

  // ── Mobile: top app bar with a hamburger that opens an off-canvas drawer ───
  return (
    <div className="cl full col" style={{ height: '100vh' }}>
      <div
        className="row center between"
        style={{
          height: 48,
          flexShrink: 0,
          background: 'var(--navy)',
          color: '#fff',
          padding: '0 10px',
        }}
      >
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          aria-label={t('open_menu')}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#fff',
            fontSize: 20,
            cursor: 'pointer',
            padding: '4px 8px',
            lineHeight: 1,
          }}
        >
          ☰
        </button>
        <img src="/brand/lockup-on-navy.svg" alt="Jalupro Iberia" style={{ height: 16, width: 'auto' }} />
        {signOutBtn}
      </div>

      {content}

      {drawerOpen && (
        <>
          <div
            onClick={() => setDrawerOpen(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(7,26,49,0.55)', zIndex: 40 }}
          />
          <div style={{ position: 'fixed', top: 0, left: 0, bottom: 0, height: '100vh', zIndex: 50 }}>
            {sidebar}
          </div>
        </>
      )}
    </div>
  );
}
